import React, { useCallback, useEffect, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { Divider, IconButton, Paper, Typography } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import type { StudioPreferencesStore, StudioSettings } from '../../constants';
import BackgroundControl from './controls/BackgroundControl';
import FormatControl from './controls/FormatControl';
import LanguageControl from './controls/LanguageControl';
import LogoWatermarkControl from './controls/LogoWatermarkControl';
import ProfileControl from './controls/ProfileControl';
import TemplateControl from './controls/TemplateControl';
import PreferencesControl from './controls/PreferencesControl';

interface SectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

const Section: React.FC<SectionProps> = ({ title, children, className = '' }) => (
  <section className={`studio-sidebar-section ${className}`.trim()}>
    <Typography variant="subtitle1" component="h2">
      {title}
    </Typography>
    {children}
  </section>
);

interface StudioSidebarProps {
  intl: any;
  settings: StudioSettings;
  onChangeSettings: (partial: Partial<StudioSettings>) => void;
  preferencesStore: StudioPreferencesStore;
  selectedSlot: number;
  slotName: string;
  onSelectSlot: (slotIndex: number) => void;
  onChangeSlotName: (name: string) => void;
  onRenameSlot: () => void;
  onSavePreferences: () => void;
  onClearPreferences: () => void;
  hideProfile?: boolean;
}

const StudioSidebar: React.FC<StudioSidebarProps> = ({
  intl,
  settings,
  onChangeSettings,
  preferencesStore,
  selectedSlot,
  slotName,
  onSelectSlot,
  onChangeSlotName,
  onRenameSlot,
  onSavePreferences,
  onClearPreferences,
  hideProfile = false
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  const updateScrollHint = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }
    setShowScrollHint(panel.scrollHeight - panel.scrollTop - panel.clientHeight > 8);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(updateScrollHint);
    window.addEventListener('resize', updateScrollHint);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateScrollHint);
    };
  }, [updateScrollHint]);

  const scrollDown = () => {
    const panel = panelRef.current;
    if (panel) {
      panel.scrollBy({ top: Math.max(180, panel.clientHeight * 0.55), behavior: 'smooth' });
    }
  };

  return (
    <>
      <Paper ref={panelRef} className="studio-sidebar-panel" elevation={0} onScroll={updateScrollHint}>
        <Section title={intl.formatMessage({ id: 'hot-topic-studio-defaults' })}>
          <PreferencesControl
            intl={intl}
            preferencesStore={preferencesStore}
            selectedSlot={selectedSlot}
            slotName={slotName}
            onSelectSlot={onSelectSlot}
            onChangeSlotName={onChangeSlotName}
            onRenameSlot={onRenameSlot}
            onSave={onSavePreferences}
            onClear={onClearPreferences}
          />
        </Section>
        <Divider />
        <Section title={intl.formatMessage({ id: 'hot-topic-studio-template' })}>
          <TemplateControl template={settings.template} onChangeTemplate={template => onChangeSettings({ template })} />
        </Section>
        <Divider />
        <Section title={intl.formatMessage({ id: 'hot-topic-studio-background' })}>
          <BackgroundControl
            themeColor={settings.themeColor}
            onChangeThemeColor={themeColor => onChangeSettings({ themeColor })}
          />
        </Section>
        <Divider />
        <Section title={intl.formatMessage({ id: 'hot-topic-studio-watermark' })}>
          <LogoWatermarkControl
            logo={settings.watermarkLogo}
            onChangeLogo={watermarkLogo => onChangeSettings({ watermarkLogo })}
          />
        </Section>
        <Divider />
        <Section title={intl.formatMessage({ id: 'hot-topic-studio-language' })}>
          <LanguageControl locale={settings.locale} onChangeLocale={locale => onChangeSettings({ locale })} />
        </Section>
        <Divider />
        <Section title={intl.formatMessage({ id: 'hot-topic-studio-format' })}>
          <FormatControl format={settings.format} onChangeFormat={format => onChangeSettings({ format })} />
        </Section>
        {!hideProfile && (
          <>
            <Divider />
            <Section
              title={intl.formatMessage({ id: 'hot-topic-studio-profile' })}
              className="studio-sidebar-format-section"
            >
              <ProfileControl
                profileName={settings.profileName}
                onChangeProfileName={profileName => onChangeSettings({ profileName })}
              />
            </Section>
          </>
        )}
      </Paper>
      {showScrollHint && (
        <div className="studio-sidebar-scroll-hint">
          <IconButton
            size="small"
            aria-label={intl.formatMessage({ id: 'hot-topic-studio-scroll-more' })}
            onClick={scrollDown}
          >
            <KeyboardArrowDownRoundedIcon />
          </IconButton>
        </div>
      )}
    </>
  );
};

export default injectIntl(StudioSidebar);
